import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { take } from 'rxjs/internal/operators/take';
import { SocialApiService } from '../../../../shared/services/social-api.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { SocialExcludedProfile, SocialSettings } from '../../../../shared/interfaces/social.interfaces';

// Admin > Socijalni mrezi > "Postavki" (section 5.5). Auto-accept defaults OFF on the backend
// (SocialSettings entity) - this screen only lets an admin change it, never flips it itself.
@Component({
	selector: 'app-social-settings',
	standalone: true,
	imports: [CommonModule, FormsModule, MatButtonModule, MatInputModule, MatFormFieldModule, MatSlideToggleModule, MatProgressSpinnerModule, MatIconModule],
	templateUrl: './social-settings.component.html',
	styleUrl: './social-settings.component.scss',
})
export class SocialSettingsComponent implements OnInit {
	settings: SocialSettings | null = null;
	loading = false;
	saving = false;

	// "Isklucheni profili - za site klienti" (SOCIJALNI-FAZA1-DOPOLNUVANJA.md section 1).
	globalExcludedProfiles: SocialExcludedProfile[] = [];
	newExcludedLink = '';
	newExcludedLabel = '';

	// CLIPPING-610 point 5: inline label edit, same pattern as Dosieja's per-client list.
	editingExcludedId: string | null = null;
	editingExcludedLabel = '';

	constructor(
		private socialApi: SocialApiService,
		private sharedService: SharedService
	) {}

	ngOnInit(): void {
		this.loading = true;
		this.socialApi
			.getSettings()
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.settings = settings;
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				},
			});
		this.loadGlobalExcludedProfiles();
	}

	save(): void {
		if (!this.settings) {
			return;
		}
		this.saving = true;
		this.socialApi
			.updateSettings(this.settings)
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.settings = settings;
					this.saving = false;
					this.sharedService.showNotification.next({ message: 'Поставките се зачувани.', type: 'SUCCESS' });
				},
				error: () => {
					this.saving = false;
					this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' });
				},
			});
	}

	private loadGlobalExcludedProfiles(): void {
		this.socialApi
			.listGlobalExcludedProfiles()
			.pipe(take(1))
			.subscribe({ next: profiles => (this.globalExcludedProfiles = profiles || []), error: () => (this.globalExcludedProfiles = []) });
	}

	addGlobalExcludedProfile(): void {
		if (!this.newExcludedLink.trim()) {
			return;
		}
		this.socialApi
			.addGlobalExcludedProfile(this.newExcludedLink.trim(), this.newExcludedLabel.trim() || undefined)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.newExcludedLink = '';
					this.newExcludedLabel = '';
					this.loadGlobalExcludedProfiles();
					this.sharedService.showNotification.next({ message: 'Профилот е исклучен за сите клиенти.', type: 'SUCCESS' });
				},
				error: err => this.sharedService.showNotification.next({ message: `Не успеа додавањето. ${err?.error?.message || ''}`, type: 'ERROR' }),
			});
	}

	deleteGlobalExcludedProfile(id: string, label: string): void {
		// Point 6: confirm before delete - this is shared by every client, a wrong click is costly.
		if (!confirm(`Да се избрише исклучениот профил „${label}" за сите клиенти?`)) {
			return;
		}
		this.socialApi
			.deleteGlobalExcludedProfile(id)
			.pipe(take(1))
			.subscribe({
				next: () => this.loadGlobalExcludedProfiles(),
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа бришењето.', type: 'ERROR' }),
			});
	}

	// Point 5: edit icon left of the ✕.
	startExcludedEdit(profile: SocialExcludedProfile): void {
		this.editingExcludedId = profile.id;
		this.editingExcludedLabel = profile.label || '';
	}

	cancelExcludedEdit(): void {
		this.editingExcludedId = null;
		this.editingExcludedLabel = '';
	}

	saveExcludedEdit(): void {
		if (!this.editingExcludedId) {
			return;
		}
		const id = this.editingExcludedId;
		this.socialApi
			.updateGlobalExcludedProfile(id, this.editingExcludedLabel.trim())
			.pipe(take(1))
			.subscribe({
				next: updated => {
					this.globalExcludedProfiles = this.globalExcludedProfiles.map(p => (p.id === id ? updated : p));
					this.editingExcludedId = null;
					this.editingExcludedLabel = '';
				},
				error: () => this.sharedService.showNotification.next({ message: 'Не успеа зачувувањето.', type: 'ERROR' }),
			});
	}
}
